import React from "react";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { PermissionTransferItem } from "../../assignment/config/types";
import type { Operation } from "../services/operationService";

// ============ 树面板配置 ============
export const treeCardConfig = {
  showCheckbox: false,
  showSearch: true,
  showSelectedCount: false,
  showSaveButton: false,
  defaultExpandAll: false,
  title: "授权操作列表",
};

// ============ 树面板按钮配置 ============
export interface TreePanelButtonHandlers {
  onRefresh: () => void;
  onAdd: () => void;
}

export const getTreePanelButtons = (handlers: TreePanelButtonHandlers) => {
  return [
    {
      key: "refresh",
      text: "刷新",
      type: "text" as const,
      icon: React.createElement(ReloadOutlined),
      onClick: handlers.onRefresh,
      group: "primary" as const,
      showTextOnHover: true, // 只在悬停时显示文字
    },
    {
      key: "add",
      text: "添加",
      type: "primary" as const,
      icon: React.createElement(PlusOutlined),
      onClick: handlers.onAdd,
      group: "primary" as const,
      // 添加按钮正常显示文字，不需要 showTextOnHover
    },
  ];
};

// ============ 中文标签配置 ============
export interface ChineseLabels {
  services: Record<string, string>;
  pages: Record<string, string>;
  actions: Record<string, string>;
}

export const defaultChineseLabels: ChineseLabels = {
  services: {},
  pages: {},
  actions: {},
};

let currentChineseLabels: ChineseLabels = defaultChineseLabels;

export const setChineseLabels = (labels: Partial<ChineseLabels>) => {
  currentChineseLabels = {
    services: { ...(currentChineseLabels.services || {}), ...(labels.services || {}) },
    pages: { ...(currentChineseLabels.pages || {}), ...(labels.pages || {}) },
    actions: { ...(defaultChineseLabels.actions || {}), ...(labels.actions || {}) },
  };
};

export const getChineseLabels = (): ChineseLabels => currentChineseLabels;

export const convertPermissionsToTree = (
  permissions: Operation[],
  labels: ChineseLabels = getChineseLabels(),
  selectedPermissionId?: string
): PermissionTransferItem[] => {
  const serviceMap = new Map<string, PermissionTransferItem>();

  permissions.forEach((permission) => {
    const serviceCode =
      permission.service ||
      permission.service_name ||
      (permission.code.split(":")[0] || "unknown");

    const serviceDisplayName =
      permission.serviceName ||
      permission.service_name ||
      permission.service ||
      serviceCode;

    if (!serviceMap.has(serviceCode)) {
      const serviceChineseName =
        labels.services[serviceCode as keyof ChineseLabels["services"]];
      const serviceTitle = serviceChineseName
        ? `${serviceChineseName} (${serviceCode})`
        : `${serviceDisplayName} (${serviceCode})`;

      serviceMap.set(serviceCode, {
        id: `service_${serviceCode}`,
        key: `service_${serviceCode}`,
        title: serviceTitle,
        description: serviceChineseName || serviceDisplayName,
        type: "MODULE",
        permissionData: {
          serviceName: serviceCode,
          name: serviceDisplayName,
          code: serviceCode,
        },
        children: [],
      });
    }

    const serviceNode = serviceMap.get(serviceCode)!;
    const codeParts = permission.code.split(":");
    const page = codeParts[1] || "default";
    const action = codeParts[2] || "default";

    let pageNode = serviceNode.children?.find(
      (child) => child.permissionData?.name === page
    );

    if (!pageNode) {
      const pageTitle = page;
      pageNode = {
        id: `page_${serviceCode}_${page}`,
        key: `page_${serviceCode}_${page}`,
        title: pageTitle,
        description: page,
        type: "MODULE",
        permissionData: {
          serviceName: serviceCode,
          name: page,
          code: `${serviceCode}:${page}`,
        },
        children: [],
      };
      serviceNode.children!.push(pageNode);
    }

    const actionTitle = action;
    const permissionNode: PermissionTransferItem = {
      id: permission.id,
      key: permission.id,
      title: actionTitle,
      description: permission.description || action,
      code: permission.code,
      type: "FUNCTION",
      isAssigned: selectedPermissionId === permission.id,
      permissionData: {
        id: permission.id,
        name: permission.name,
        code: permission.code,
        description: permission.description,
        serviceName: serviceCode,
        page,
        action,
        type: "FUNCTION",
        isActive:
          permission.is_active ??
          (permission as unknown as { isActive?: boolean }).isActive ??
          true,
        created_at: permission.created_at,
        updated_at: permission.updated_at,
      },
    };

    pageNode.children!.push(permissionNode);
  });

  return Array.from(serviceMap.values());
};


